import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  CreditCard,
  Loader2,
  Minus,
  MousePointerClick,
  Palette,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Upload,
} from 'lucide-react';
import {
  VSS_STORE_SLUG,
  getActiveProducts,
  createCheckoutOrder,
  startCommerceCheckout,
  payOrderWithCredits,
  formatCashPrice,
  formatDualCashPrice,
  getUsdPriceCents,
  canPayWithCredits,
  type CommerceProduct,
} from '@rehub/commerce';
import { supabase } from '../services/supabase';
import { ROOM_TYPES, FURNITURE_STYLES, STAGING_TIERS } from '../constants';

const STEPS = ['Services', 'Files', 'Brief', 'Review'] as const;

const HOW_IT_WORKS = [
  {
    icon: MousePointerClick,
    title: 'Choose & order',
    body: 'Pick how many images you want staged and check out in under a minute.',
  },
  {
    icon: Upload,
    title: 'Send your photos',
    body: 'Upload now or from your order page — plus any style notes for your brief.',
  },
  {
    icon: Palette,
    title: 'Our team stages them',
    body: 'Professional designers hand-perfect every room for a photorealistic finish.',
  },
  {
    icon: Check,
    title: 'Get listing-ready shots',
    body: 'Delivered to your order page, MLS-ready and free to revise until you’re happy.',
  },
] as const;

const TRUST_SIGNALS = [
  { icon: Clock, label: '24–48h turnaround' },
  { icon: RefreshCw, label: 'Free revisions' },
  { icon: ShieldCheck, label: 'Human quality-checked' },
] as const;

function HowItWorks() {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 sm:p-6">
      <p className="mb-5 text-xs font-semibold uppercase tracking-widest text-violet-300/90">
        How done-for-you works
      </p>
      <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {HOW_IT_WORKS.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={s.title} className="relative flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-600/15 text-violet-300 ring-1 ring-violet-500/30">
                  <Icon size={18} />
                  <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-[11px] font-bold text-white">
                    {i + 1}
                  </span>
                </span>
              </div>
              <p className="text-sm font-semibold text-white">{s.title}</p>
              <p className="text-xs leading-relaxed text-slate-400">{s.body}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const APP_LOGOS: Record<string, string> = {
  staging: '/rehub/virtualstaging.png',
  imagemotion: '/rehub/imagemotion.png',
  hdrphoto: '/rehub/hdrphoto.png',
  tours: '/rehub/360tours.png',
  sketchplan: '/rehub/planos.png',
  cgi: '/rehub/virtualstaging.png',
};

function productLogo(product: CommerceProduct): string {
  const targetApp = product.metadata?.target_app as string | undefined;
  if (targetApp && APP_LOGOS[targetApp]) return APP_LOGOS[targetApp];
  if (product.category === 'Virtual Staging') return APP_LOGOS.staging;
  if (product.category === 'ImageMotion') return APP_LOGOS.imagemotion;
  if (product.category === 'HDRPhoto') return APP_LOGOS.hdrphoto;
  if (product.category === '360 Tour Build') return APP_LOGOS.tours;
  if (product.category === 'Floor Plans') return APP_LOGOS.sketchplan;
  if (product.category === 'CGI') return APP_LOGOS.cgi;
  return APP_LOGOS.staging;
}

function PriceTag({ product }: { product: CommerceProduct }) {
  const { gbp, usd } = formatDualCashPrice(product);
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="text-lg font-semibold text-white">{gbp}</span>
      {usd && <span className="text-sm text-slate-400">{usd}</span>}
      {product.credit_price != null && product.credit_price > 0 && (
        <span className="text-xs text-violet-300/80">· {product.credit_price} credits</span>
      )}
    </div>
  );
}

function StagingPanel({
  product,
  quantity,
  onChange,
}: {
  product: CommerceProduct;
  quantity: number;
  onChange: (next: number) => void;
}) {
  const logo = productLogo(product);
  const active = quantity > 0;
  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-5 transition-all ${
        active
          ? 'border-violet-500 bg-violet-950/40 shadow-lg shadow-violet-900/20 ring-1 ring-violet-500/50'
          : 'border-slate-800 bg-slate-900/60'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-700/80 bg-slate-950/80 p-1.5">
          <img src={logo} alt="" className="h-full w-full object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug text-white">{product.title}</p>
          {product.description && (
            <p className="mt-1 text-xs leading-relaxed text-slate-400">{product.description}</p>
          )}
        </div>
      </div>

      <div className="mt-4 border-t border-slate-800/80 pt-3">
        <PriceTag product={product} />
        <span className="mt-0.5 block text-[11px] text-slate-500">per image</span>
      </div>

      <div className="mt-5 flex items-center justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Images</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label={`Remove one ${product.title}`}
            onClick={() => onChange(Math.max(0, quantity - 1))}
            disabled={quantity === 0}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 bg-slate-900 text-slate-300 transition-colors hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Minus size={16} />
          </button>
          <span className="w-8 text-center text-xl font-semibold text-white tabular-nums">
            {quantity}
          </span>
          <button
            type="button"
            aria-label={`Add one ${product.title}`}
            onClick={() => onChange(quantity + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-violet-500 bg-violet-600 text-white transition-colors hover:bg-violet-500"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

interface ServicesWizardProps {
  userId: string;
  userCredits: number;
}

export default function ServicesWizard({ userId, userCredits }: ServicesWizardProps) {
  const [step, setStep] = useState(0);
  const [products, setProducts] = useState<CommerceProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [selected, setSelected] = useState<Record<string, number>>({});
  const [brief, setBrief] = useState('');
  const [roomType, setRoomType] = useState('');
  const [furnitureStyle, setFurnitureStyle] = useState('');
  const [stagingTier, setStagingTier] = useState('standard');
  const [contactEmail, setContactEmail] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [paying, setPaying] = useState(false);
  const [useCredits, setUseCredits] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getActiveProducts(supabase, VSS_STORE_SLUG, 'Virtual Staging')
      .then((all) => setProducts(all.filter((p) => p.fulfilment_type === 'managed_service')))
      .finally(() => setLoadingProducts(false));
  }, []);

  const selectedProducts = products.filter((p) => selected[p.id]);
  const totalImages = selectedProducts.reduce((sum, p) => sum + (selected[p.id] || 0), 0);
  const totalPence = selectedProducts.reduce(
    (sum, p) => sum + p.cash_price_pence * (selected[p.id] || 1),
    0
  );
  const totalUsdCents = selectedProducts.reduce(
    (sum, p) => sum + (getUsdPriceCents(p) ?? 0) * (selected[p.id] || 1),
    0
  );
  const totalCredits = selectedProducts.reduce(
    (sum, p) => sum + (p.credit_price ?? 0) * (selected[p.id] || 1),
    0
  );
  const canUseCredits =
    totalCredits > 0 &&
    selectedProducts.every((p) => canPayWithCredits(p)) &&
    userCredits >= totalCredits;

  const setQty = (p: CommerceProduct, next: number) => {
    setSelected((prev) => {
      const updated = { ...prev };
      if (next <= 0) delete updated[p.id];
      else updated[p.id] = next;
      return updated;
    });
  };

  const handlePay = async () => {
    if (selectedProducts.length === 0) {
      setError('Select at least one service.');
      return;
    }
    setPaying(true);
    setError(null);

    const items = selectedProducts.map((p) => ({
      product_id: p.id,
      quantity: selected[p.id] || 1,
    }));

    const roomTypeLabel = ROOM_TYPES.find((r) => r.id === roomType)?.label ?? null;
    const styleLabel = FURNITURE_STYLES.find((s) => s.id === furnitureStyle)?.label ?? null;
    const tierLabel = STAGING_TIERS.find((t) => t.id === stagingTier)?.label ?? null;

    const preferenceLines = [
      roomTypeLabel && `Room type: ${roomTypeLabel}`,
      styleLabel && `Furniture style: ${styleLabel}`,
      tierLabel && `Quality tier: ${tierLabel}`,
    ].filter(Boolean) as string[];

    const customerNotes = [preferenceLines.join('\n'), brief.trim()]
      .filter(Boolean)
      .join('\n\n');

    const createResult = await createCheckoutOrder(supabase, {
      storeSlug: VSS_STORE_SLUG,
      items,
      userId,
      customerNotes: customerNotes || null,
      projectDetails: {
        contact_email: contactEmail,
        wizard: true,
        room_type: roomType || null,
        room_type_label: roomTypeLabel,
        furniture_style: furnitureStyle || null,
        furniture_style_label: styleLabel,
        staging_tier: stagingTier || null,
        staging_tier_label: tierLabel,
      },
    });

    if (!createResult.success || !createResult.order_id) {
      setPaying(false);
      setError(createResult.error || 'Could not create order');
      return;
    }

    const orderId = createResult.order_id;

    if (useCredits && canUseCredits) {
      const payResult = await payOrderWithCredits(supabase, orderId, userId);
      setPaying(false);
      if (!payResult.success) {
        setError(payResult.error || 'Credit payment failed');
        return;
      }
      window.location.href = `/orders/${orderId}?paid=1`;
      return;
    }

    const origin = window.location.origin;
    const checkout = await startCommerceCheckout(supabase, {
      orderId,
      userId,
      successUrl: `${origin}/orders/${orderId}?paid=1`,
      cancelUrl: `${origin}/services?cancelled=1`,
    });
    setPaying(false);

    if (checkout.paid) {
      window.location.href = `/orders/${orderId}?paid=1`;
      return;
    }
    if (checkout.error || !checkout.url) {
      setError(checkout.error || 'Checkout failed');
      return;
    }
    window.location.href = checkout.url;
  };

  return (
    <div className="min-h-full bg-black pb-12 text-slate-200">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <a href="/" className="mb-6 flex items-center gap-1 text-sm text-slate-400 hover:text-white">
          <ArrowLeft size={16} /> Back to app
        </a>

        <div className="mb-8 overflow-hidden rounded-3xl border border-violet-500/20 bg-gradient-to-br from-violet-950/50 via-slate-900/60 to-slate-950 p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-600/15 px-3 py-1 text-xs font-semibold text-violet-200">
            <Sparkles size={13} className="shrink-0" />
            Done for you · staged by our design team
          </div>
          <h1 className="mt-4 max-w-2xl text-3xl font-bold leading-tight text-white sm:text-4xl">
            Professional virtual staging,{' '}
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              handled for you
            </span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Skip the editing. Send us your empty rooms and our team hand-stages every photo into a
            photorealistic, listing-ready shot. You only pick the images and the style.
          </p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {TRUST_SIGNALS.map((t) => {
              const Icon = t.icon;
              return (
                <span
                  key={t.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-700/80 bg-slate-950/50 px-3 py-1.5 text-xs font-medium text-slate-200"
                >
                  <Icon size={14} className="shrink-0 text-violet-300" />
                  {t.label}
                </span>
              );
            })}
          </div>
        </div>

        <ol className="mb-8 flex items-center">
          {STEPS.map((label, i) => {
            const done = i < step;
            const active = i === step;
            const isLast = i === STEPS.length - 1;
            return (
              <li key={label} className="flex flex-1 items-center last:flex-none">
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                      active
                        ? 'bg-violet-600 text-white ring-4 ring-violet-600/20'
                        : done
                          ? 'bg-violet-700/80 text-white'
                          : 'bg-slate-800 text-slate-500'
                    }`}
                  >
                    {done ? <Check size={15} /> : i + 1}
                  </div>
                  <span
                    className={`text-[11px] font-medium ${
                      active ? 'text-violet-300' : done ? 'text-slate-400' : 'text-slate-600'
                    }`}
                  >
                    {label}
                  </span>
                </div>
                {!isLast && (
                  <div
                    className={`mx-2 mb-5 h-0.5 flex-1 rounded-full transition-colors ${
                      done ? 'bg-violet-700/70' : 'bg-slate-800'
                    }`}
                  />
                )}
              </li>
            );
          })}
        </ol>

        {error && (
          <div className="mb-4 rounded-xl border border-rose-800 bg-rose-950/30 p-3 text-sm text-rose-300">
            {error}
          </div>
        )}

        {step === 0 && (
          <div className="space-y-6">
            <HowItWorks />
            <div>
              <h2 className="text-lg font-semibold text-white">Choose your staging</h2>
              <p className="mt-1 text-sm text-slate-400">
                Set how many images you’d like our team to stage. You can mix options.
              </p>
            </div>
            {loadingProducts ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-56 animate-pulse rounded-2xl border border-slate-800 bg-slate-900/50"
                  />
                ))}
              </div>
            ) : products.length === 0 ? (
              <p className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-center text-sm text-slate-400">
                No staging options are available right now.
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {products.map((p) => (
                  <StagingPanel
                    key={p.id}
                    product={p}
                    quantity={selected[p.id] ?? 0}
                    onChange={(next) => setQty(p, next)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
            <p className="mb-4 text-sm text-slate-400">
              Optional — you can also upload files from your order page after payment.
            </p>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => setPendingFiles(Array.from(e.target.files ?? []))}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
                const dropped = Array.from(e.dataTransfer.files ?? []);
                if (dropped.length) setPendingFiles(dropped);
              }}
              className={`flex w-full items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-10 transition-colors ${
                dragActive
                  ? 'border-violet-500 text-violet-300'
                  : 'border-slate-600 text-slate-400 hover:border-violet-500 hover:text-violet-300'
              }`}
            >
              <Upload size={20} />
              {pendingFiles.length
                ? `${pendingFiles.length} file(s) selected`
                : dragActive
                  ? 'Drop files here'
                  : 'Choose files or drag and drop'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-300">Room type</label>
                <select
                  value={roomType}
                  onChange={(e) => setRoomType(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white outline-none focus:border-violet-500"
                >
                  <option value="">No preference</option>
                  {ROOM_TYPES.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-300">Furniture style</label>
                <select
                  value={furnitureStyle}
                  onChange={(e) => setFurnitureStyle(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white outline-none focus:border-violet-500"
                >
                  <option value="">No preference</option>
                  {FURNITURE_STYLES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-300">Quality tier</label>
                <select
                  value={stagingTier}
                  onChange={(e) => setStagingTier(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white outline-none focus:border-violet-500"
                >
                  {STAGING_TIERS.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Pick defaults for the whole order — add per-image specifics in the brief below.
            </p>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Project brief</label>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={5}
                className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white outline-none focus:border-violet-500"
                placeholder="Property address, style preferences, deadlines…"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Contact email</label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white outline-none focus:border-violet-500"
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
            <h2 className="font-semibold text-white">Order summary</h2>
            <ul className="space-y-3 text-sm">
              {selectedProducts.map((p) => {
                const qty = selected[p.id] || 1;
                const usdUnit = getUsdPriceCents(p);
                const { gbp, usd } = formatDualCashPrice({
                  cash_price_pence: p.cash_price_pence * qty,
                  metadata: usdUnit != null ? { usd_price_cents: usdUnit * qty } : {},
                });
                return (
                  <li key={p.id} className="flex items-start justify-between gap-3">
                    <span className="text-slate-300">
                      {p.title} <span className="text-slate-500">× {qty}</span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-white">{gbp}</span>
                      {usd && <span className="block text-xs text-slate-500">{usd}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
            {(roomType || furnitureStyle || stagingTier) && (
              <div className="space-y-1 border-t border-slate-800 pt-3 text-sm">
                {roomType && (
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Room type</span>
                    <span className="text-slate-300">
                      {ROOM_TYPES.find((r) => r.id === roomType)?.label}
                    </span>
                  </div>
                )}
                {furnitureStyle && (
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Furniture style</span>
                    <span className="text-slate-300">
                      {FURNITURE_STYLES.find((s) => s.id === furnitureStyle)?.label}
                    </span>
                  </div>
                )}
                {stagingTier && (
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Quality tier</span>
                    <span className="text-slate-300">
                      {STAGING_TIERS.find((t) => t.id === stagingTier)?.label}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-between border-t border-slate-700 pt-3 font-medium">
              <span>Total</span>
              <span className="text-right">
                <span className="block text-white">{formatCashPrice(totalPence, 'GBP')}</span>
                {totalUsdCents > 0 && (
                  <span className="block text-xs text-slate-500">
                    {formatCashPrice(totalUsdCents, 'USD')}
                  </span>
                )}
              </span>
            </div>
            {totalCredits > 0 && (
              <p className="text-sm text-slate-400">or {totalCredits} credits</p>
            )}
            {canUseCredits && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={useCredits}
                  onChange={(e) => setUseCredits(e.target.checked)}
                />
                Pay with credits ({userCredits} available)
              </label>
            )}
            {!canUseCredits && totalCredits > 0 && userCredits < totalCredits && (
              <p className="text-sm text-amber-400">
                Insufficient credits ({userCredits}/{totalCredits}). Use the credits button in the
                header to buy more.
              </p>
            )}
          </div>
        )}

      </div>

      <div className="sticky bottom-0 z-20 border-t border-slate-800 bg-slate-950/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center gap-4 px-4 py-3.5 sm:px-6">
          <button
            type="button"
            disabled={step === 0}
            onClick={() => setStep((s) => s - 1)}
            className="shrink-0 rounded-lg px-3 py-2 text-sm text-slate-400 transition-colors hover:text-white disabled:pointer-events-none disabled:opacity-0"
          >
            <span className="flex items-center gap-1">
              <ArrowLeft size={16} /> Back
            </span>
          </button>

          <div className="min-w-0 flex-1">
            {totalImages > 0 ? (
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-xs text-slate-400">
                  {totalImages} image{totalImages === 1 ? '' : 's'}
                </span>
                <span className="text-lg font-semibold text-white">
                  {formatCashPrice(totalPence, 'GBP')}
                </span>
                {totalUsdCents > 0 && (
                  <span className="text-xs text-slate-500">
                    {formatCashPrice(totalUsdCents, 'USD')}
                  </span>
                )}
                {totalCredits > 0 && (
                  <span className="text-xs text-violet-300/80">· {totalCredits} credits</span>
                )}
              </div>
            ) : (
              <span className="text-xs text-slate-500">Add images to get started</span>
            )}
          </div>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 0 && selectedProducts.length === 0}
              className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-900/30 transition-all hover:from-violet-500 hover:to-fuchsia-500 hover:shadow-violet-800/40 disabled:cursor-not-allowed disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-400 disabled:shadow-none"
            >
              {step === 0 ? 'Continue to checkout' : 'Continue'}
              <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              disabled={paying}
              onClick={handlePay}
              className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-900/30 transition-all hover:from-violet-500 hover:to-fuchsia-500 hover:shadow-violet-800/40 disabled:opacity-60"
            >
              {paying ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CreditCard size={16} />
              )}
              {useCredits && canUseCredits ? 'Place order · pay with credits' : 'Place order · pay by card'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
